// assistantMode.js — WHAT THE ASSISTANT PRODUCES (AI_ASSISTANT_SPEC v1 §1 and
// §12.1; DESIGN-WL-ASSISTANT-MAIN-VIEW-1). Nika 2026-09-29: "dropdown should
// contain [Assistant, Dashboard, Tools (platform apps), Apps (frontend),
// Presentation]" — five modes, a CLOSED vocabulary on the client and the server.
//
// ONE state key, `root.assistantMode`:
//   'assistant'     ASSISTANT — a conversation; each answer is text plus
//                   embedded, live UI blocks (the default until 2026-10-22;
//                   now the mode the home's Dashboard gives back when no
//                   mode before Fly is known, functions/homeView.js)
//   'dashboard'     DASHBOARD — a generated board of widgets (the ai.boards
//                   path, functions/assistantBoard.js: one fast call, no tools)
//   'tools'         TOOLS — work INSIDE the installed platform apps: open the
//                   right app page with filters, run app actions, find and
//                   install store apps (every write confirmed)
//   'apps'          APPS — a FRONTEND app: a small app surface built inside
//                   this workspace (create_extension), previewed inline, then
//                   "Save as app"; a full app project only when asked
//   'presentation'  PRESENTATION — a live deck (the ai.boards deck shape,
//                   functions/assistantDeck.js: one fast call, no tools)
//   'fly'           FLY — every ask composes a PAGE, like a Fly site
//                   (keepz--fly): eyebrow + title, a checked opening, live
//                   sections, follow-ups, one session of pages (functions/
//                   assistantFly.js; the ai.boards fly shape). THE DEFAULT:
//                   Nika 2026-10-17, "fly mode will run as mode for now,
//                   later we can make it default"; 2026-10-22, "ok make fly
//                   default view" — a person who never chose a mode is in Fly
//                   mode, and their home opens on Fly (functions/homeView.js).
// The chip shipped to dev only, so a stored 'apps' simply means frontend apps
// now (no migration, §12.1). MODE is WHAT the AI produces; PLACEMENT
// (`assistantDock`, functions/assistantDock.js) is WHERE it shows.
//
// PERSISTED PER USER: localStorage paints first (`symbols_assistant_mode`);
// the `user_preferences` row (`assistantMode`) follows the person to every
// device. The record wins once it has loaded; a local choice with no record
// migrates up (loadPrivateData).
//
// WRITES ARE DECISIONS (the home-prefs durability law, functions/homeLook.js):
// at once, never debounced, SINGLE-FLIGHT on the queue every writer of the
// user_preferences row shares (`root.__navbarSaveQueue` — the navbar tabs, the
// assistant starters, the home look), keyed the way they key it. The update
// REPLACES the row, so the payload is composed at SEND time from the live row
// (a click-time payload would drop what another writer landed meanwhile); a
// row that has not loaded is read first (a write never replaces the account's
// other preferences with one key); a decision superseded while it waited sends
// nothing (the newest one lands last); the confirmed value is adopted only
// when no newer decision waits.
//
// AN EXPLICIT CHOICE WINS OVER EVERY DEFAULT: this writer is the only thing
// that sets the key. The AI may answer ONE ask in another mode when the ask is
// clearly a board or an app action (and it says so) — that is a per-answer
// route in homeAiSend, never a write here.
//
// Frank note: el.call serializes each registered export on its own (FA204),
// so every export here is self-contained — the few literals are restated.

// setAssistantMode(mode) via el.call — or setAssistantMode(root, mode, sdk)
// from shell code. The mode chip (design-d2, packages/shared) reads
// `root.assistantMode` and calls `el.call('setAssistantMode', mode)`.
//
// `{ local: true }` (el.call('setAssistantMode', mode, { local: true }), or the
// 4th argument from shell code) is a HOME TAB, not a choice (BLD-13, Nika
// 2026-10-25: "Change only the home view"): the live mode and its Fly side
// effects, nothing persisted — no record, no device cache, no "before Fly".
// The home's tabs (functions/homeView.js) hold that view in `_homeMode` and
// homeModeSync gives the person's own pick back off the home. Every other
// call is a choice: it persists, and it ends a home view (`_homeMode: null`).
export function setAssistantMode(a, b, c, d) {
  const modes = ['assistant', 'dashboard', 'tools', 'apps', 'presentation', 'fly']
  const viaCall = typeof a === 'string' || a == null
  const value = viaCall ? a : b
  const opts = (viaCall ? b : d) || null
  const local = !!(opts && typeof opts === 'object' && opts.local)
  const self = this && typeof this === 'object' ? this : null
  const root = viaCall ? (self && self.state && self.state.root) || null : a
  const sdk = viaCall
    ? (self && (self.sdk || (self.context && self.context.sdk))) || null
    : c || null
  if (!modes.includes(value)) {
    // A silent ignore costs a live-test cycle (the dock's AI-308 lesson): say
    // what was refused and what is accepted.
    console.warn(
      `[shell] setAssistantMode ignored invalid value "${String(value)}" — ` +
        "accepted values: 'assistant' | 'dashboard' | 'tools' | 'apps' | 'presentation' | 'fly'"
    )
    return
  }
  if (!root || typeof root.update !== 'function') return
  const prefs = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
  const previousMode = root.assistantMode
  const previousFlyWorkspace = root._aiFlyWorkspace
  const patch = { assistantMode: value }
  if (!local) {
    if (prefs && prefs.assistantMode !== value) patch.userPrefs = { ...prefs, assistantMode: value }
    if (root._homeMode) patch._homeMode = null
  }
  root.update(patch, { preventFetch: true })
  const fns = (self && self.context && self.context.functions) || {}
  if (self && typeof self.call === 'function') {
    // A stored mode can also enter through the shell mount. If that reactive
    // edge already initialized Fly during update, do not repeat its prompt.
    if (
      value === 'fly' &&
      root._aiFlyWorkspace === previousFlyWorkspace &&
      typeof fns.assistantFlyWorkspaceEnter === 'function'
    )
      self.call('assistantFlyWorkspaceEnter')
    if (value !== 'fly' && previousMode === 'fly') {
      if (typeof fns.assistantFlyWorkspaceClose === 'function')
        self.call('assistantFlyWorkspaceClose')
      if (typeof fns.assistantFlyClose === 'function') self.call('assistantFlyClose')
    }
  }
  if (local) return
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('symbols_assistant_mode', value)
      // The mode Fly was entered FROM, so the home's Dashboard choice can give
      // it back (functions/homeView.js homeViewSelect). A per-device
      // convenience: on another device Dashboard falls back to 'assistant'.
      if (value === 'fly' && modes.includes(previousMode) && previousMode !== 'fly')
        localStorage.setItem('symbols_assistant_mode_before_fly', previousMode)
    }
  } catch (err) {
    console.warn('[shell] assistant-mode cache write failed:', err && err.message)
  }
  // Every explicit choice is SENT, the active value included: the loaded copy
  // of the row can be stale (a boot read that predates an earlier write lands
  // after it), so "the copy already says so" proves nothing about the record.
  // The write is idempotent; a burst collapses to its newest decision below.
  if (!sdk || typeof sdk.execute !== 'function') return
  const w = root.workspace
  const key =
    String(
      (w && (w.id || w._id)) || (root.jwtClaims && root.jwtClaims.active_workspace_id) || ''
    ) || 'user'
  if (!root.__navbarSaveQueue) root.__navbarSaveQueue = new Map()
  const queue = root.__navbarSaveQueue
  root.__assistantModeSeq = (root.__assistantModeSeq || 0) + 1
  const seq = root.__assistantModeSeq
  const prior = queue.get(key) || Promise.resolve()
  const task = prior
    .catch(() => {})
    .then(async () => {
      if (seq !== root.__assistantModeSeq) return null
      // PER-PATH PATCH — names only `assistantMode`, so a second tab's write to
      // any OTHER key never gets overwritten (CORE-USERPREFS-PATCH-PER-PATH-1).
      const row = await sdk.execute('workspaceProject.userPreferences', 'patch', {
        patch: { set: { assistantMode: value } }
      })
      if (seq === root.__assistantModeSeq) {
        const now = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
        root.update(
          {
            userPrefs: now
              ? { ...now, assistantMode: value }
              : row && typeof row === 'object'
                ? row
                : { assistantMode: value }
          },
          { preventFetch: true }
        )
      }
      return row
    })
  queue.set(key, task)
  return task.catch((err) => {
    console.warn('[shell] user_preferences assistantMode save failed:', err && err.message)
    return null
  })
}

// The stored mode on this device, or null when the person never chose.
export const readStoredAssistantMode = (storage) => {
  try {
    const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null)
    const v = store ? store.getItem('symbols_assistant_mode') : null
    return ['assistant', 'dashboard', 'tools', 'apps', 'presentation', 'fly'].includes(v) ? v : null
  } catch (_) {
    return null
  }
}

// The mode to return to when the person leaves Fly from the home's switch:
// the one Fly was entered from on this device (setAssistantMode keeps it),
// else the default 'assistant'. Never 'fly' itself.
export const assistantModeBeforeFly = (storage) => {
  try {
    const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null)
    const v = store ? store.getItem('symbols_assistant_mode_before_fly') : null
    return ['assistant', 'dashboard', 'tools', 'apps', 'presentation'].includes(v) ? v : 'assistant'
  } catch (_) {
    return 'assistant'
  }
}

// The mode a root shows: its key when valid, else the default 'fly'.
export const assistantModeOf = (root) => {
  const v = root && typeof root === 'object' ? root.assistantMode : null
  return ['assistant', 'dashboard', 'tools', 'apps', 'presentation', 'fly'].includes(v) ? v : 'fly'
}

// Boot reconcile, once the user_preferences row has landed. PURE:
//   { apply: mode }    the record holds a choice that differs from the state
//   { migrate: mode }  no record choice; this device holds one — persist it up
//   null               nothing to do
export const reconcileAssistantModeFromPrefs = (currentMode, prefs, storedMode) => {
  const modes = ['assistant', 'dashboard', 'tools', 'apps', 'presentation', 'fly']
  const norm = (v) => (modes.includes(v) ? v : null)
  const recorded = norm(prefs && typeof prefs === 'object' ? prefs.assistantMode : null)
  if (recorded) return recorded !== currentMode ? { apply: recorded } : null
  const local = norm(storedMode)
  return local ? { migrate: local } : null
}
